import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import CustomModalWrapper from './RouterPromptWrapper';
import styles from './routerPromptStyles.module.scss';
import { Button } from 'components/quashed';
import history from 'helpers/history';
import classnames from 'classnames';
import {
  ADVICE_ROUTE,
  MARKET_SCAN_PURCHASE_ROUTE,
  MARKET_SCAN_STATUS_ROUTE,
  POLICY_DETAILS_ROUTE,
} from 'constants/routes';

// Blog used as a guide for this component
// https://blog.formpl.us/how-to-create-a-custom-route-blocking-modal-in-react-9306139ee3c7

interface IProps {
  className?: string;
  providerName?: string;
  modalClassName?: string;
  closeOnOutsideClick?: boolean;
  disableClose?: boolean;
  navigateTo?: string;
  showPurchaseSurveyForm: boolean;
  render?(continueNavigation: () => void, closeModal: () => void): JSX.Element;
}

const WHITELIST_ROUTES = [
  ADVICE_ROUTE,
  MARKET_SCAN_STATUS_ROUTE,
  MARKET_SCAN_PURCHASE_ROUTE,
  POLICY_DETAILS_ROUTE.replace(':id', ''),
];

const RouterPrompt = ({
  providerName,
  children,
  className,
  closeOnOutsideClick,
  disableClose,
  modalClassName,
  navigateTo,
  render,
  showPurchaseSurveyForm,
}: PropsWithChildren<IProps>) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const location = useLocation();
  const [lastLocation, setLastLocation] = useState(location);
  const confirmNavigation = useRef(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const showModal = (nextLocation: typeof location) => {
    openModal();
    setLastLocation(nextLocation);
  };

  useEffect(() => {
    if (showPurchaseSurveyForm) {
      setIsModalOpen(true);
    }
  }, [providerName, showPurchaseSurveyForm]);

  const handleBlockedRoute = (nextLocation: typeof location) => {
    // handle routes that don't want to have popups
    const checkWhitelist = WHITELIST_ROUTES.find((route) =>
      nextLocation.pathname.includes(route),
    );
    if (checkWhitelist) {
      return true;
    }

    if (!confirmNavigation.current) {
      showModal(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = () => {
    confirmNavigation.current = true;
    closeModal();

    // navigate to selected route
    if (navigateTo) {
      history.push(navigateTo);
    } else if (lastLocation) {
      history.push(
        lastLocation.pathname + lastLocation.search,
        lastLocation.state,
      );
    }
  };

  const getCloseModalHandler = () => {
    if (closeOnOutsideClick) {
      return closeModal;
    }
    if (!disableClose) {
      return handleConfirmNavigationClick;
    }
    return undefined;
  };

  return (
    <>
      <Prompt when={true} message={handleBlockedRoute} />
      {isModalOpen && (
        <CustomModalWrapper
          className={modalClassName}
          closeModal={getCloseModalHandler}
        >
          <div className={classnames(styles.modalContainer, className)}>
            {render ? (
              render(handleConfirmNavigationClick, closeModal)
            ) : (
              <>
                {children}
                <div className={styles.modalButtons}>
                  <Button
                    className={styles.btn}
                    text="Yes"
                    onClick={handleConfirmNavigationClick}
                  />
                  <Button
                    className={styles.btn}
                    text="No"
                    onClick={closeModal}
                  />
                </div>
              </>
            )}
          </div>
        </CustomModalWrapper>
      )}
    </>
  );
};

export default RouterPrompt;
